import type { CoTenantRole, LeaseObjectType } from './Enums';
import type { FileAttachment } from './attachments';

/**
 * Leases at `leases/{leaseKey}`: flat root indexed on `tenantKey` and
 * `buildingKey` (like projects). A lease ties a tenant to an object — a
 * parking spot, a storage unit or "other" — with amount, interval, period and
 * contract files. `tenantKey` is the primary contract holder (invoice and
 * contact); further people on the same lease live in `coTenants`. Status is
 * derived from the dates for display; the rented state of a parking spot or
 * storage unit is the explicit `currentLeaseKey` on that node, kept in step
 * by leasesApi and the daily reconcile function.
 * Schema shared with mf-functions and iOS — see docs/features/parking-tenants
 * (D2, D3) and docs/features/storage-units/03-plan.md (D6, D7) in mf-workspace.
 */

export type IntervalUnit = 'month' | 'year';

export interface IncomeInterval {
    count: number;   // 1, 2, 3, etc.
    unit: IntervalUnit;
}

export interface LeaseObject {
    type: LeaseObjectType;
    /** parkingSpot only: area key under buildings/{buildingKey}/parking. */
    areaKey?: string;
    /** parkingSpot: spot key under that area. storageUnit: unit key under buildings/{buildingKey}/storageUnits. */
    key?: string;
    /** Display snapshot ("Garage -1 · Plats 12", "Källare (-01) · Förråd 12") or the free text for `other`. */
    label?: string;
}

/**
 * One rent period: `amount` applies from `from` (YYYY-MM-DD) until the next
 * entry's `from`. Entries are push-keyed; the lease's `amount` is always the
 * current value and the newest entry repeats it (units plan, phase 3).
 */
export interface AmountHistoryEntry {
    from: string;
    amount: number;
}

/**
 * Contract terms, mainly for lokalhyresavtal (units plan, phase 3). All
 * optional; `noticeMonths` drives the "Uppsägning senast" date.
 */
export interface LeaseTerms {
    /** Uppsägningstid in months before `endDate`. */
    noticeMonths?: number;
    /** Förlängningstid in months when the lease is not terminated in time. */
    renewalMonths?: number;
    /** Indexklausul (KPI) in the contract. */
    indexClause?: boolean;
    /** Frivillig skattskyldighet för moms on the premises. */
    vatRegistered?: boolean;
}

/**
 * One extra person on the lease, keyed by their `tenants/{tenantKey}` record.
 * The primary tenant (`tenantKey`) is never in this map. One rent, never
 * split per person: a medhyresgäst is jointly liable for the whole amount.
 */
export interface CoTenant {
    role: CoTenantRole;
}

export type CoTenantsMap = { [tenantKey: string]: CoTenant };

export interface LeaseData extends LeaseTerms {
    /** Primary tenant (kontraktsinnehavare): invoice recipient and contact. */
    tenantKey: string;
    /** Further people on the lease (medhyresgäster and medboende); absent when none. */
    coTenants?: CoTenantsMap;
    buildingKey: string;
    object: LeaseObject;
    /** Current rent. Earlier rents live in `amountHistory`. */
    amount: number;
    interval: IncomeInterval;
    /** Rent changes over time; absent when the rent never changed. */
    amountHistory?: { [entryKey: string]: AmountHistoryEntry };
    /** YYYY-MM-DD */
    startDate: string;
    /** YYYY-MM-DD, inclusive. Absent = open-ended. */
    endDate?: string;
    /** Storage path mirrors the DB path: leases/{leaseKey}/contracts/{attachmentKey}. */
    contracts?: { [attachmentKey: string]: FileAttachment };
    comment?: string;
    createdBy: string;      // UID
    createdWhen: string;    // Stockholm 'YYYY-MM-DD hh:mm:ss'
    updatedBy?: string;
    updatedWhen?: string;
}

export interface Lease extends LeaseData {
    key: string;
}

/** Editable lease fields (everything except key, contracts, history and audit stamps). */
export interface LeaseInput extends LeaseTerms {
    tenantKey: string;
    /** Extra people on the lease; empty or absent clears the map on update. */
    coTenants?: CoTenantsMap;
    buildingKey: string;
    object: LeaseObject;
    amount: number;
    interval: IncomeInterval;
    startDate: string;
    endDate?: string;
    comment?: string;
    /**
     * Update only: the date the new `amount` applies from. When the amount
     * differs from the stored one, the change is recorded in `amountHistory`
     * from this date (default today). Ignored on create.
     */
    amountEffectiveFrom?: string;
}

/** Helper to format interval as Swedish text */
export const formatInterval = (interval: IncomeInterval): string => {
    const { count, unit } = interval;
    if (unit === 'month') {
        if (count === 1) return 'per månad';
        if (count === 3) return 'per kvartal';
        if (count === 6) return 'per halvår';
        return `var ${count}:e månad`;
    } else {
        if (count === 1) return 'per år';
        return `var ${count}:e år`;
    }
};

/** Helper to calculate yearly income from interval */
export const calculateYearlyIncome = (amount: number, interval: IncomeInterval): number => {
    const { count, unit } = interval;
    if (unit === 'month') {
        return (amount * 12) / count;
    } else {
        return amount / count;
    }
};
